/**
 * CuadraturaDelDia — lo recaudado contra lo girado, compañía por compañía.
 *
 * ES EL CONTROL DEL PASO 2 (§3 del spec), y era el hallazgo #1 del rediseño:
 * ninguna pantalla verificaba que lo recaudado de un día hubiera salido girado
 * ese día. Se liquidaba a diario y se controlaba al cierre del mes.
 *
 * LOS CINCO MONTOS ENCADENAN, y por eso ninguna columna sobra:
 *
 *   cobrado − retenido = recaudado      lo que de verdad entra a la cuenta
 *   debe girarse = cobrado              el bruto completo (R6)
 *   debe girarse − girado = diferencia  con signo, por compañía (R8)
 *
 * El hueco entre «recaudado» y «debe girarse» es la comisión del PSP, y es la
 * plata que la tesorería tiene que reponer para que el giro alcance. Mostrar
 * sólo el bruto lo escondía: la pantalla decía «hay que girar $100.000» sin
 * decir que en la cuenta hay $96.500.
 *
 * NINGÚN TOTAL DE DESCUADRE (R8). Un neto miente: con $50.000 de menos a una y
 * $50.000 de más a otra el total da cero y la pantalla diría que el día cuadra,
 * mientras hay plata en la cuenta equivocada. Las dos direcciones van separadas
 * y nunca restadas.
 *
 * ÁMBAR NO ES ROJO. Un giro que todavía no sale es lo normal a media mañana;
 * uno hecho por menos ya costó dinero. Si lo pendiente pintara rojo, la pantalla
 * estaría en rojo toda la mañana y el rojo dejaría de significar algo.
 *
 * SÓLO LEE. La acción de girar vive en el paso 6, arriba. Esto verifica.
 *
 * @see payment/src/services/cuadratura-dia.service.ts — las reglas y los estados
 * @see src/styles/yoolpay-cierre.css — las clases `.cw-*`, portadas del prototipo
 */
import React from 'react';

import { StatusPill } from './cierre/ds';
import { CIFRA } from '../../../lib/yoolpay/estados';
import { fmtClp, fmtClpPiso, fmtDelta } from '../../../lib/yoolpay/formato';

/**
 * Estado → pill del DS y color de la cifra girada.
 *
 * `StatusPill` lleva el color adentro: si el estado se mapea a la variante
 * correcta, el color sale gratis y sale bien. Por eso acá no hay paleta.
 */
const ESTADO = {
  girado: { pill: 'activo', texto: 'Cuadra', color: CIFRA.verde },
  pendiente: { pill: 'pausado', texto: 'Por girar', color: CIFRA.ambar },
  parcial: { pill: 'error', texto: 'Faltan', color: CIFRA.rojo },
  excedido: { pill: 'error', texto: 'Sobran', color: CIFRA.rojo },
  sin_valorizar: { pill: 'pausado', texto: 'Sin valorizar', color: CIFRA.ambar },
  sin_giro: { pill: 'procesando', texto: 'Informativo', color: CIFRA.apagado },
};

const COLS = '1.4fr 150px 112px 104px 112px 118px 108px 118px';

/** La nota bajo el nombre: por qué esta fila está como está. */
function notaDe(f) {
  if (f.estado === 'sin_giro') return 'Cobró en su propia cuenta · no se gira';
  if (f.estado === 'sin_valorizar') {
    return `${f.cobros_sin_monto} cobro${f.cobros_sin_monto === 1 ? '' : 's'} sin monto`;
  }
  if (f.pago_propio_clp > 0) {
    return `Además ${fmtClp(f.pago_propio_clp)} cobrados por su cuenta`;
  }
  if (f.estado === 'excedido' && f.debe_girarse_clp === 0) {
    return 'Se giró sin recaudación este día';
  }
  return null;
}

/** Una celda de plata. Alineada a la derecha y con cifras tabulares. */
function Monto({ children, size = 12.5, weight = 500, color }) {
  return (
    <span
      className="cw-mono"
      style={{ fontSize: size, fontWeight: weight, textAlign: 'right', color }}
    >
      {children}
    </span>
  );
}

/**
 * @param {object[]} giros `cierre.giros` — con qué referencia salió cada uno y quién lo
 *   registró. Vive acá desde que se retiró el bloque «Giros del día», que repetía compañía
 *   y monto: lo único propio de aquel bloque era ese rastro, y no se pierde.
 */
export default function CuadraturaDelDia({ cuadratura, giros = [] }) {
  const giroDe = new Map((giros ?? []).map((g) => [g.compania_id, g]));
  // `null` es «no se pudo calcular», no «no hay nada»: sin tesorería payment no
  // afirma nada, y un bloque en cero se leería como que todo cuadra.
  if (!cuadratura) return null;

  const { filas, cuadra, companias_descuadradas: descuadradas } = cuadratura;
  const completa = cuadratura.estimacion_completa;
  const porGirar = cuadratura.por_girar_clp;

  // TRES ESTADOS Y NO DOS. «No hay descuadre» NO es «está girado»: el 19-08 dev
  // tiene $961.796 sin salir y cero descuadres, y pintar eso de verde con la
  // palabra «Cuadra» es afirmar un hecho que no ocurrió. El verde comunica un
  // HECHO consumado —la regla de `estados.js`— así que lo pendiente va ámbar.
  const hayGiros = filas.some((f) => f.girado_clp > 0);
  const dia = !cuadra
    ? { texto: 'No cuadra', color: CIFRA.rojo,
      nota: `${descuadradas} compañía${descuadradas === 1 ? '' : 's'} sin cuadrar` }
    : porGirar > 0
      ? { texto: 'Por girar', color: CIFRA.ambar, nota: `${fmtClp(porGirar)} sin salir` }
      : hayGiros
        ? { texto: 'Girado', color: CIFRA.verde, nota: 'Todo salió a quien corresponde' }
        : { texto: 'Sin giros', color: CIFRA.apagado, nota: 'Nada que girar este día' };

  return (
    <div className="cw-card">
      <div className="cw-card-head">
        <div>
          <h2 className="cw-card-title">Cuadratura del día · recaudado contra giros</h2>
          <p className="cw-card-sub">
            La liquidación es diaria, así que la jornada tiene que cerrar sola. A cada
            compañía se le gira <strong style={{ color: 'var(--yool-gray1)' }}>completo</strong>
            {' '}lo que se cobró para ella: los fees no se descuentan del giro, se facturan
            aparte. Si una compañía no cuadra, se ve acá y no un mes después.
          </p>
        </div>
        <span style={{ textAlign: 'right', flexShrink: 0 }}>
          <span className="cw-th" style={{ display: 'block' }}>{dia.nota}</span>
          <span
            className="cw-mono"
            style={{
              display: 'block', fontSize: 24, fontWeight: 700, marginTop: 4, color: dia.color,
            }}
          >
            {dia.texto}
          </span>
        </span>
      </div>

      {filas.length === 0 ? (
        <div style={{ padding: '18px 24px' }}>
          <span className="cw-aviso baja">
            <span className="cw-ai"><span>i</span></span>
            <span>Este día no registró recaudación ni giros.</span>
          </span>
        </div>
      ) : (
        <>
          {/* LA TABLA SCROLLEA DENTRO DE SU TARJETA, no la página. Ocho columnas de ancho
              fijo no caben en un monitor normal, y una grilla en px no se encoge. */}
          <div className="cw-tabla">
          <div className="cw-row cw-head-row" style={{ gridTemplateColumns: COLS }}>
            <span className="cw-th">Compañía</span>
            {/* EL CORTE COMERCIAL, que antes vivía en una tabla aparte con las mismas
                filas: cuánto de lo cobrado es venta nueva y cuánto es cobranza. */}
            <span className="cw-th">Venta nueva / Cobranza</span>
            <span className="cw-th" style={{ textAlign: 'right' }}>Cobrado</span>
            <span className="cw-th" style={{ textAlign: 'right' }}>Retenido PSP</span>
            <span className="cw-th" style={{ textAlign: 'right' }}>Recaudado</span>
            <span className="cw-th" style={{ textAlign: 'right' }}>Debe girarse</span>
            <span className="cw-th" style={{ textAlign: 'right' }}>Girado</span>
            <span className="cw-th" style={{ textAlign: 'right' }}>Verificación</span>
          </div>

          {filas.map((f, i) => {
            const e = ESTADO[f.estado] ?? ESTADO.sin_valorizar;
            const nota = notaDe(f);
            const noCuadra = f.estado === 'parcial' || f.estado === 'excedido';
            // El monto va PEGADO al estado: «Faltan» a secas obliga a mirar otra
            // columna para saber cuánto.
            const etiqueta = noCuadra
              ? `${e.texto} ${fmtClp(Math.abs(f.diferencia_clp))}`
              : e.texto;

            return (
              <div
                key={f.compania_id}
                className={`cw-row${f.estado === 'girado' ? ' done' : ''}`
                  + `${noCuadra || f.estado === 'sin_valorizar' ? ' obs' : ''}`
                  + `${i === filas.length - 1 ? ' last' : ''}`}
                style={{ gridTemplateColumns: COLS, alignItems: 'center' }}
              >
                <span style={{ minWidth: 0, paddingRight: 12 }}>
                  {/* El uuid completo en el `title`: es lo que se pega en un ticket
                      cuando la aseguradora reclama por su giro (R21). */}
                  <span
                    title={f.compania_id}
                    style={{
                      display: 'block', fontSize: 13.5, fontWeight: 600,
                      letterSpacing: '-.01em', minWidth: 0, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {f.compania_nombre ?? f.compania_id}
                  </span>
                  {nota ? (
                    <span
                      style={{
                        display: 'block', fontSize: 11.5,
                        color: 'var(--cw-tinta-suave)', marginTop: 2,
                      }}
                    >
                      {nota}
                    </span>
                  ) : null}
                </span>

                {/* Las dos líneas de negocio, una sobre otra: ocupan una columna en vez
                    de dos y se leen como lo que son, un reparto del cobrado. Lo que no
                    suma es lo indeterminado, que NO se reparte a ojo. */}
                <span style={{ fontSize: 11.5, lineHeight: 1.45 }}>
                  <span style={{ display: 'block', color: 'var(--yool-gray1)' }}>
                    Nueva <span className="cw-mono">{fmtClp(f.venta_nueva_clp)}</span>
                  </span>
                  <span style={{ display: 'block', color: 'var(--yool-muted)' }}>
                    Cobranza <span className="cw-mono">{fmtClp(f.cobranza_clp)}</span>
                  </span>
                </span>

                {/* R10: con cobros sin monto la cifra es un PISO y va con `≥`.
                    Nunca `$0`, que se leería como «no hay nada que girar». */}
                <Monto>{fmtClpPiso(f.cobrado_clp, f.estimacion_completa)}</Monto>
                {/* `—` y no `$0`: un cero diría que el PSP no retuvo nada, que es lo
                    contrario de «no sabemos cuánto retuvo». */}
                <Monto color="var(--yool-muted)">{fmtClp(f.retenido_clp)}</Monto>
                <Monto color="var(--yool-muted)">{fmtClp(f.recaudado_clp)}</Monto>
                <Monto weight={600} color="var(--yool-gray1)">
                  {fmtClpPiso(f.debe_girarse_clp, f.estimacion_completa)}
                </Monto>
                {/* EL RASTRO DEL GIRO, debajo de la cifra: con qué número salió del banco
                    y quién lo registró. Es lo que cruza contra la cartola y lo que se mira
                    cuando la aseguradora llama a preguntar. */}
                <span style={{ textAlign: 'right' }}>
                  <Monto size={13} weight={700} color={e.color}>{fmtClp(f.girado_clp)}</Monto>
                  {giroDe.get(f.compania_id) ? (
                    <span
                      title={giroDe.get(f.compania_id).registrado_por ?? ''}
                      style={{
                        display: 'block', fontSize: 10.5, color: 'var(--cw-tinta-suave)',
                        marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ref {giroDe.get(f.compania_id).referencia}
                      {giroDe.get(f.compania_id).registrado_por
                        ? ` · ${String(giroDe.get(f.compania_id).registrado_por).split('@')[0]}`
                        : ''}
                    </span>
                  ) : null}
                </span>
                <span style={{ textAlign: 'right' }}>
                  <StatusPill estado={e.pill}>{etiqueta}</StatusPill>
                </span>
              </div>
            );
          })}

          </div>

          {/* LAS DOS DIRECCIONES POR SEPARADO, jamás restadas (R8). */}
          <div className="cw-card-foot">
            <span style={{ fontSize: 12.5, color: 'var(--yool-gray1)' }}>
              {!cuadra
                ? 'Revisar antes de cerrar el día: la diferencia va con signo por compañía.'
                : porGirar > 0
                  ? 'Sin descuadres, pero el día todavía no está girado.'
                  : hayGiros
                    ? 'Todo lo cobrado hoy salió girado a quien corresponde.'
                    : 'Este día no tuvo giros que hacer.'}
              {completa === false
                ? ' Hay cobros sin monto, así que las cifras son un piso.'
                : ''}
            </span>
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 20 }}>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span className="cw-th">Falta girar</span>
                <span
                  className="cw-mono"
                  style={{
                    fontSize: 16, fontWeight: 700,
                    color: porGirar > 0 ? CIFRA.ambar : CIFRA.tinta,
                  }}
                >
                  {fmtDelta(porGirar)}
                </span>
              </span>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span className="cw-th">Girado de más</span>
                <span
                  className="cw-mono"
                  style={{
                    fontSize: 16, fontWeight: 700,
                    color: cuadratura.girado_de_mas_clp > 0 ? CIFRA.rojo : CIFRA.tinta,
                  }}
                >
                  {fmtDelta(-cuadratura.girado_de_mas_clp)}
                </span>
              </span>
            </span>
          </div>
        </>
      )}
    </div>
  );
}
